import { DialogCoinsBadge } from "../common_ui_elements/dialog_coins_badge.js";
import { Vector2 } from "../../common/vector2.js";
import { NineSliceDictionary } from "../../image_loader/nine_slicer/nine_slice_dictionary.js";
import { BackButtonHandler } from "../../navigation/back_button_handler.js";
import { BasicEventsController } from "../../touch_events_controller/basic_events_controller.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { ResourceManager } from "../../resource_loader/resource_manager.js";
import { audioController } from "../../audio/audio_controller.js";
import { AudioClips } from "../../audio/audio_constants.js";
import { ImagesHolder } from "../../image_loader/images_holder.js";
import { DevToDev } from "../../plugins/dev_to_dev_plugin.js";
import { CrashBreadcrumbs } from "../../crash/breadcrumbs.js";
import { collectDomImageUrls, getDomHost, resyncDomHost, showDomHostForRaster } from "../dom_backend/dom_backend.js";
import { isNullCanvasContext } from "../dom_backend/null_canvas_context.js";
import { BackgroundWork } from "../../utils/background_work.js";
import { isMemoryConstrained } from "../../utils/memory_policy.js";
import {
  appearKeyframes,
  closeKeyframes,
  fadeBackdrop,
  primeNodeForRaster,
  primeScaleFor,
  runSurfaceAnimation,
  snapBackdrop,
  surfaceAnimationsSupported,
  unprimeNode,
  waitForNextFrame,
  waitForPresentedFrame,
  type SurfaceAppearMotion,
} from "../dom_backend/surface_motion.js";

export interface DialogPrewarmFactory
{
  key: string;
  create: () => AbstractDialog;
}

export class DialogManager
{
  public static ctx: CanvasRenderingContext2D;

  public static readonly BACKDROP_FADE_MS = 144;

  private static dialog_stack: AbstractDialog[] = [];
  private static canvas: HTMLCanvasElement;
  private static events_controller: BasicEventsController;
  private static width:number;
  private static height:number;
  private static overlay: HTMLElement;
  public static ns_dictionary = NineSliceDictionary.values;

  private static is_down_outside_of_dialog:boolean = false;
  // The coin badge sits outside the dialog body (on the menu's counter spot).
  private static pressed_coins_badge: DialogCoinsBadge | null = null;
  private static is_route_transition_active:boolean = false;
  private static pending_route_dialog: AbstractDialog | null = null;
  private static pending_close_after_transition:boolean = false;
  private static pending_open_dialogs: AbstractDialog[] = [];
  private static interaction_blocked_until_ms:number = 0;
  private static overlay_is_visible:boolean = false;
  private static last_open_at = 0;
  private static dom_host: HTMLDivElement | null = null;
  private static dom_last_background = "";

  public static init( ctx: CanvasRenderingContext2D, width:number, height:number ): void
  {
    this.ctx = ctx;
    this.canvas = ctx.canvas;
    this.width = width;
    this.height = height;
    this.overlay = document.getElementById( 'dialogOverlay' ) as HTMLElement
    this.dom_host = isNullCanvasContext( ctx ) ? getDomHost( this.canvas ) : null;

    this.events_controller = new BasicEventsController( this.canvas );
    this.events_controller.init(
      ( p:Vector2 ) => this.handleDown( p ),
      ( p:Vector2 ) => this.handleUp  ( p ),
      ( p:Vector2 ) => this.handleMove( p )
    );
  }

  public static setScreenSize( width: number, height: number ): void
  {
    const changed = width !== this.width || height !== this.height;
    this.width = width;
    this.height = height;
    if ( changed )
      this.releasePool( "screen size changed" );

    // Called after the dialog canvas buffer was reallocated (blank). render()
    // only repaints dirty elements, so without this the next dirty child (e.g.
    // the name input blurring when the keyboard hides) is drawn alone on an
    // empty canvas over the dim overlay.
    if ( this.dialog_stack.length > 0 )
      this.topDialog().invalidateSubtree();
  }

  public static update( now:number ):void
  {
    if ( this.dialog_stack.length == 0 )
      return;

    const top_dialog = this.topDialog();
    top_dialog.update( now );

    if ( top_dialog.isCloseAnimationFinished( now ) )
    {
      this.finalizeTopDialogClose();
    }
  }

  public static render():void
  {
    if ( this.dialog_stack.length == 0 )
      return;

    const top_dialog = this.topDialog();
    if ( !top_dialog.shouldBeRendered() )
    {
      return;
    }

    if ( this.dom_host )
    {
      this.renderDom( top_dialog );
      return;
    }

    this.ctx.clearRect( 0, 0, this.width, this.height );

    const bg_img_id = top_dialog.getBackgroundImageId();
    if ( bg_img_id !== null )
    {
      const img = ImagesHolder.getImage( bg_img_id );
      if ( img instanceof ImageBitmap || ( img as object ) instanceof HTMLImageElement )
      {
        const scale = Math.max( this.width / img.width, this.height / img.height );
        const draw_w = img.width * scale;
        const draw_h = img.height * scale;
        const draw_x = ( this.width - draw_w ) / 2;
        const draw_y = ( this.height - draw_h ) / 2;
        this.ctx.drawImage( img, draw_x, draw_y, draw_w, draw_h );
      }
    }

    top_dialog.render();
  }

  private static renderDom( top_dialog: AbstractDialog ): void
  {
    const host = this.dom_host as HTMLDivElement;

    for ( const dialog of this.dialog_stack )
    {
      if ( !dialog.isDomMode() )
        continue;
      dialog.mountDomHost( this.canvas );
      const display = dialog === top_dialog ? "" : "none";
      const node = dialog.getDomNode();
      if ( node.style.display !== display )
        node.style.display = display;
    }

    const bg_img_id = top_dialog.getBackgroundImageId();
    const bg_url = bg_img_id !== null ? ImagesHolder.getUrl( bg_img_id ) : null;
    const background = bg_url ? `url("${bg_url}") center / cover no-repeat` : "";
    if ( background !== this.dom_last_background )
    {
      this.dom_last_background = background;
      host.style.background = background;
    }

    top_dialog.render();
  }

  public static isDomMode(): boolean
  {
    return this.dom_host !== null;
  }

  private static handleDown( p:Vector2 ): void
  {
    if ( this.isInteractionBlocked() )
      return;

    if ( this.dialog_stack.length === 0 )
      return;

    const badge = this.coinsBadgeAt( p );
    if ( badge )
    {
      this.pressed_coins_badge = badge;
      badge.onTouchStart( badge.toParentSpace( p ), performance.now() );
      return;
    }

    if ( this.isInsideOpenedDialog( p ) )
    {
      const top_dialog = this.topDialog();
      top_dialog.onTouchStart( p, performance.now() );
      return;
    }

    this.is_down_outside_of_dialog = true;
  }

  private static coinsBadgeAt( p: Vector2 ): DialogCoinsBadge | null
  {
    for ( const child of this.topDialog().getChildren() )
    {
      if ( child instanceof DialogCoinsBadge && child.hitGlobal( p ) )
        return child;
    }
    return null;
  }

  private static handleUp( p:Vector2 ): void
  {
    if ( this.isInteractionBlocked() )
      return;

    if ( this.dialog_stack.length === 0 )
      return;

    const badge = this.pressed_coins_badge;
    if ( badge )
    {
      this.pressed_coins_badge = null;
      badge.onTouchEnd( badge.toParentSpace( p ), performance.now(), p );
      return;
    }

    const top_dialog = this.topDialog();
    top_dialog.onTouchEnd( p, performance.now() );

    if ( this.isInsideOpenedDialog( p ) )
    {
      this.is_down_outside_of_dialog = false;
      return;
    }

    if ( !this.is_down_outside_of_dialog )
      return;

    this.is_down_outside_of_dialog = false;
    if ( this.topDialog().getCloseOnBackgroundClick() )
      this.closeDialog( 'background_tap' );
  }

  private static handleMove( p:Vector2 ): void
  {
    if ( this.isInteractionBlocked() )
      return;

    if ( this.dialog_stack.length === 0 )
      return;

    if ( this.isInsideOpenedDialog( p ) )
    {
      const top_dialog = this.topDialog();
      top_dialog.onTouchMove( p, performance.now() );
      return;
    }
  }

  private static isInsideOpenedDialog( p:Vector2 ): boolean
  {
    if ( this.dialog_stack.length === 0 )
      return false;

    const top_dialog = this.topDialog();
    return top_dialog.containsPointGlobal( p );
  }

  public static openDialog( dialog: AbstractDialog ): void
  {
    if ( this.isAnyDialogOpened() && ( this.is_route_transition_active || this.topDialog().isClosing() ) )
    {
      this.pending_open_dialogs.push( dialog );
      return;
    }
    this.openDialogInternal( dialog, true, true );
  }

  private static pool = new Map<string, AbstractDialog>();
  private static readonly POOL_LIMIT = 12;
  private static readonly POOL_LIMIT_MEMORY_CONSTRAINED = 4;

  private static poolingEnabled(): boolean
  {
    return this.dom_host !== null;
  }

  public static takePooled( key: string ): AbstractDialog | null
  {
    const pooled = this.pool.get( key );
    if ( !pooled )
      return null;
    this.pool.delete( key );
    if ( this.isDialogOpen( pooled ) || pooled === this.prewarming_dialog )
      return null;
    return pooled;
  }

  public static acquire<P>( key: string, params: P, factory: () => AbstractDialog ): AbstractDialog
  {
    const pooled = this.takePooled( key );
    if ( pooled )
    {
      pooled.resetForReuse();
      try
      {
        pooled.onReuse( params );
        return pooled;
      }
      catch ( e )
      {
        console.warn( `[DialogManager] onReuse failed for ${key}, rebuilding`, e );
        this.destroyDialog( pooled );
      }
    }
    return factory();
  }

  public static openPooled<P>( key: string, params: P, factory: () => AbstractDialog ): AbstractDialog
  {
    const dialog = this.acquire( key, params, factory );
    this.openDialog( dialog );
    return dialog;
  }

  public static routePooled<P>( key: string, params: P, factory: () => AbstractDialog ): AbstractDialog
  {
    const dialog = this.acquire( key, params, factory );
    this.routeToDialog( dialog );
    return dialog;
  }

  private static recycle( dialog: AbstractDialog ): boolean
  {
    if ( !this.poolingEnabled() || !dialog.isPoolable() || !dialog.isDomMode() )
      return false;

    const key = dialog.poolKey();
    const previous = this.pool.get( key );
    if ( previous && previous !== dialog )
      this.destroyDialog( previous );
    this.pool.delete( key );

    try
    {
      dialog.onPooled();
      dialog.hideIntoPool();
    }
    catch ( e )
    {
      console.warn( `[DialogManager] onPooled failed for ${key}, destroying`, e );
      this.destroyDialog( dialog );
      return false;
    }

    this.pool.set( key, dialog );
    const limit = isMemoryConstrained() ? this.POOL_LIMIT_MEMORY_CONSTRAINED : this.POOL_LIMIT;
    while ( this.pool.size > limit )
    {
      const oldest_key = this.pool.keys().next().value as string;
      const oldest = this.pool.get( oldest_key ) as AbstractDialog;
      this.pool.delete( oldest_key );
      this.destroyDialog( oldest );
    }
    return true;
  }

  private static destroyDialog( dialog: AbstractDialog ): void
  {
    try { dialog.cleanup(); }
    catch ( e ) { console.warn( "DialogManager: cleanup failed", e ); }
  }

  public static releasePool( reason: string ): void
  {
    if ( this.pool.size === 0 )
      return;
    console.info( `[DialogManager] releasing dialog pool (${this.pool.size}): ${reason}` );
    for ( const dialog of this.pool.values() )
      this.destroyDialog( dialog );
    this.pool.clear();
  }

  public static pooledNames(): string[]
  {
    return [ ...this.pool.keys() ];
  }

  public static routeToDialog( dialog: AbstractDialog ): void
  {
    if ( this.is_route_transition_active )
    {
      // Keep only the latest requested destination to avoid transition queue growth.
      this.pending_route_dialog = dialog;
      return;
    }

    if ( !this.isAnyDialogOpened() )
    {
      this.openDialog( dialog );
      return;
    }

    const top_dialog = this.topDialog();
    if ( top_dialog.isClosing() )
    {
      this.pending_route_dialog = dialog;
      this.is_route_transition_active = true;
      return;
    }

    this.pending_route_dialog = dialog;
    this.is_route_transition_active = true;
    this.is_down_outside_of_dialog = false;
    this.interaction_blocked_until_ms = 0;

    // Route transition uses softer scale, then opens next dialog without overlay flicker.
    top_dialog.onDialogCloseRequested( 'route_transition' );
    CrashBreadcrumbs.add( 'dialog', `close ${top_dialog.name?.() ?? 'unknown'} (route_transition)` );
    top_dialog.startCloseAnimation( 0.92, 112 );
  }

  private static openDialogInternal(
    dialog: AbstractDialog,
    should_animate_overlay: boolean,
    should_animate_dialog: boolean
  ): void
  {
    this.abortDomPrewarm();
    if ( this.isAnyDialogOpened() && this.topDialog().isClosing() )
      this.finalizeTopDialogClose( false );

    CrashBreadcrumbs.add( 'dialog', `open ${dialog.name?.() ?? 'unknown'}` );
    this.dialog_stack.push( dialog );
    this.last_open_at = performance.now();
    if ( this.dom_host && dialog.isDomMode() )
      dialog.mountDomHost( this.canvas );
    if ( should_animate_dialog )
      dialog.onDialogOpened();
    else
    {
      this.cancelCanvasAnim();
      this.canvas.style.opacity = "";
    }
    dialog.onDialogShown();
    this.show( should_animate_overlay );
    BackButtonHandler.getInstance().updateBackButtonVisibility();
    try { void DevToDev.sendEvent(`dialog_open_${dialog.name?.() ?? 'unknown'}`); } catch {}
    audioController.playSound( AudioClips.UI_CLICK );
  }

  public static closeAfterTransition(): void
  {
    if ( !this.is_route_transition_active )
    {
      this.closeDialog();
      return;
    }
    this.pending_close_after_transition = true;
  }

  public static closeAllDialogs(): void
  {
    this.pending_route_dialog = null;
    this.pending_open_dialogs.length = 0;
    this.pending_close_after_transition = false;
    this.is_route_transition_active = false;
    this.interaction_blocked_until_ms = 0;
    if ( this.isAnyDialogOpened() )
      CrashBreadcrumbs.add( 'dialog', `close_all [${this.dialog_stack.map( d => d.name?.() ?? 'unknown' ).join( ',' )}]` );
    while ( this.isAnyDialogOpened() )
    {
      const closed = this.dialog_stack.pop();
      closed?.onDialogCloseRequested( 'close_all' );
      this.callOnClose( closed );
      try { void DevToDev.sendEvent(`dialog_close_${closed?.name?.() ?? 'unknown'}`); } catch {}
    }

    this.ctx.clearRect( 0, 0, this.width, this.height );
    this.hide();
    BackButtonHandler.getInstance().updateBackButtonVisibility();
  }

  public static closeDialog( reason: string = 'programmatic' ): void
  {
    if ( this.is_route_transition_active )
      return;

    if ( !this.isAnyDialogOpened() )
      return;

    const top_dialog = this.topDialog();
    if ( top_dialog.isClosing() )
    {
      return;
    }

    top_dialog.onDialogCloseRequested( reason );
    CrashBreadcrumbs.add( 'dialog', `close ${top_dialog.name?.() ?? 'unknown'} (${reason})` );
    void audioController.playSound( AudioClips.UI_CLICK );

    const is_last_dialog = this.dialog_stack.length === 1;
    if ( !is_last_dialog )
      this.is_route_transition_active = true;

    top_dialog.startCloseAnimation();
    if ( is_last_dialog )
      fadeBackdrop( this.overlay, 0, this.BACKDROP_FADE_MS );
  }

  public static closeDialogInstance( dialog: AbstractDialog, reason: string = 'programmatic' ): void
  {
    const index = this.dialog_stack.indexOf( dialog );
    if ( index < 0 )
      return;

    if ( index === this.dialog_stack.length - 1 )
    {
      this.closeDialog( reason );
      return;
    }

    this.dialog_stack.splice( index, 1 );
    dialog.onDialogCloseRequested( reason );
    CrashBreadcrumbs.add( 'dialog', `close ${dialog.name?.() ?? 'unknown'} (${reason}, buried)` );
    this.callOnClose( dialog );
    try { void DevToDev.sendEvent(`dialog_close_${dialog.name?.() ?? 'unknown'}`); } catch {}
  }

  public static isDialogOpen( dialog: AbstractDialog ): boolean
  {
    return this.dialog_stack.indexOf( dialog ) >= 0;
  }

  private static finalizeTopDialogClose( reveal_previous: boolean = true ): void
  {
    this.finalizeTopDialogCloseInternal( reveal_previous );
    this.flushPendingOpens();
  }

  private static flushPendingOpens(): void
  {
    if ( this.pending_open_dialogs.length === 0 )
      return;
    if ( this.isAnyDialogOpened() && ( this.is_route_transition_active || this.topDialog().isClosing() ) )
      return;
    const next = this.pending_open_dialogs.shift()!;
    this.openDialogInternal( next, true, true );
  }

  private static finalizeTopDialogCloseInternal( reveal_previous: boolean ): void
  {
    if ( !this.isAnyDialogOpened() )
      return;

    const closed = this.dialog_stack.pop();
    this.callOnClose( closed );
    try { void DevToDev.sendEvent(`dialog_close_${closed?.name?.() ?? 'unknown'}`); } catch {}
    if ( this.pending_route_dialog )
    {
      const next_dialog = this.pending_route_dialog;
      this.pending_route_dialog = null;
      this.openDialogInternal( next_dialog, false, false );
      this.is_route_transition_active = false;
      return;
    }

    if ( this.pending_close_after_transition )
    {
      this.pending_close_after_transition = false;
      this.is_route_transition_active = false;
      if ( this.isAnyDialogOpened() )
        this.closeDialog();
      return;
    }

    if ( !this.isAnyDialogOpened() )
    {
      this.ctx.clearRect( 0, 0, this.width, this.height );
      this.cancelCanvasAnim();
      this.canvas.style.opacity = "";
      this.canvas.style.display = "none";
      this.overlay_is_visible = false;
      requestAnimationFrame( () => {
        if ( !this.overlay_is_visible )
        {
          snapBackdrop( this.overlay, 0 );
          this.overlay.style.display = "none";
        }
      } );
      BackButtonHandler.getInstance().updateBackButtonVisibility();
      this.is_route_transition_active = false;
      return;
    }

    if ( reveal_previous )
    {
      const revealed_dialog = this.topDialog();
      revealed_dialog.playReturnToTopAnimation();
      snapBackdrop( this.overlay, 1 );
      this.interaction_blocked_until_ms = performance.now() + 112;
    }

    BackButtonHandler.getInstance().updateBackButtonVisibility();
    this.is_route_transition_active = false;
  }

  private static canvas_anim: Animation | null = null;
  private static readonly EASE_OUT_QUAD = "cubic-bezier(0.25, 0.46, 0.45, 0.94)";

  public static useCssAnimations( dialog: AbstractDialog ): boolean
  {
    return !this.dom_host
      && dialog.getBackgroundImageId() === null
      && typeof this.canvas?.animate === "function"
      && localStorage.getItem( "perf_diag_dialog_canvas_anim" ) !== "1";
  }

  public static useDomAnimations( dialog: AbstractDialog ): boolean
  {
    return this.dom_host !== null
      && dialog.isDomMode()
      && surfaceAnimationsSupported( dialog.getDomNode() );
  }

  private static cancelCanvasAnim(): void
  {
    if ( !this.canvas_anim )
      return;

    this.canvas_anim.cancel();
    this.canvas_anim = null;
  }

  private static runCanvasAnim(
    dialog: AbstractDialog,
    keyframes: Keyframe[],
    duration_ms: number,
    cancel_on_finish: boolean
  ): void
  {
    this.cancelCanvasAnim();

    const center = dialog.getCenterGlobal();
    this.canvas.style.transformOrigin = `${center.x}px ${center.y}px`;

    const anim = this.canvas.animate( keyframes, {
      duration: duration_ms,
      fill: "both",
    } );
    this.canvas_anim = anim;

    if ( cancel_on_finish )
    {
      anim.onfinish = () =>
      {
        if ( this.canvas_anim === anim )
        {
          anim.cancel();
          this.canvas_anim = null;
        }
      };
    }
  }

  public static animateAppearCss(
    dialog: AbstractDialog,
    from_scale: number,
    overshoot_scale: number,
    final_scale: number,
    scale_up_duration: number,
    settle_duration: number
  ): void
  {
    // The first GPU raster of a freshly drawn dialog can stall the main thread
    // for hundreds of ms (cold per dialog type). Keep the canvas invisible for
    // one presented frame so the stall happens behind the dim overlay, then run
    // the full appear animation from its start.
    this.cancelCanvasAnim();
    this.canvas.style.opacity = "0";

    requestAnimationFrame( () => requestAnimationFrame( () =>
    {
      if ( this.topDialogSafe() !== dialog || dialog.isClosing() )
      {
        this.canvas.style.opacity = "";
        return;
      }

      this.canvas.style.opacity = "";
      const total = scale_up_duration + settle_duration;
      this.runCanvasAnim( dialog, [
        { transform: `scale(${from_scale})`, opacity: 0, offset: 0, easing: this.EASE_OUT_QUAD },
        { transform: `scale(${overshoot_scale})`, opacity: 1, offset: scale_up_duration / total, easing: this.EASE_OUT_QUAD },
        { transform: `scale(${final_scale})`, opacity: 1, offset: 1 },
      ], total, true );
      dialog.onCssAppearStarted();
    } ) );
  }

  public static animateAppearDom( dialog: AbstractDialog, motion: SurfaceAppearMotion ): void
  {
    const node = dialog.getDomNode();
    dialog.cancelSurfaceAnimation();
    primeNodeForRaster( node, primeScaleFor( motion ) );

    const wait = dialog.wasReused() ? waitForNextFrame() : waitForPresentedFrame();
    void wait.then( () =>
    {
      if ( this.topDialogSafe() !== dialog || dialog.isClosing() || !this.isDialogOpen( dialog ) )
      {
        if ( !dialog.isClosing() )
          unprimeNode( node );
        return;
      }

      unprimeNode( node );
      const { keyframes, duration_ms } = appearKeyframes( motion );
      dialog.setSurfaceAnimation( runSurfaceAnimation( node, keyframes, duration_ms, true ) );
      dialog.onDomAppearStarted( keyframes, duration_ms );
    } );
  }

  // The appear counterpart of `animateCloseDom`: a DOM layer the dialog keeps outside its node
  // (a scroll list in <body>) runs the dialog's own appear keyframes around the dialog's center.
  public static animateAppearDomLayer(
    element: HTMLElement,
    dialog: AbstractDialog,
    keyframes: Keyframe[],
    duration_ms: number
  ): Animation | null
  {
    element.style.opacity = "";
    if ( typeof element.animate !== "function" )
      return null;

    const center = dialog.getCenterGlobal();
    const canvas_left = Number.parseFloat( this.canvas.style.left ) || 0;
    const canvas_top = Number.parseFloat( this.canvas.style.top ) || 0;
    const element_left = Number.parseFloat( element.style.left ) || 0;
    const element_top = Number.parseFloat( element.style.top ) || 0;
    element.style.transformOrigin =
      `${canvas_left + center.x - element_left}px ${canvas_top + center.y - element_top}px`;

    // `backwards` only: after the animation the inline style (opacity cleared above) is the end state.
    return element.animate( keyframes, { duration: duration_ms, fill: "backwards" } );
  }

  public static animateCloseDomNode( dialog: AbstractDialog, to_scale: number, duration_ms: number ): void
  {
    const node = dialog.getDomNode();
    const keyframes = closeKeyframes( node, { to_scale, duration_ms } );
    dialog.cancelSurfaceAnimation();
    unprimeNode( node );
    dialog.setSurfaceAnimation( runSurfaceAnimation( node, keyframes, duration_ms, false ) );
  }

  public static animateReturnToTopDom( dialog: AbstractDialog, from_scale: number, final_scale: number, duration_ms: number ): void
  {
    const node = dialog.getDomNode();
    dialog.cancelSurfaceAnimation();
    unprimeNode( node );
    dialog.setSurfaceAnimation( runSurfaceAnimation( node, [
      { scale: `${from_scale} ${from_scale}`, opacity: 0, easing: this.EASE_OUT_QUAD },
      { scale: `${final_scale} ${final_scale}`, opacity: 1 },
    ], duration_ms, true ) );
  }

  private static topDialogSafe(): AbstractDialog | null
  {
    return this.dialog_stack.length > 0 ? this.dialog_stack[this.dialog_stack.length - 1] : null;
  }

  public static animateCloseCss(
    dialog: AbstractDialog,
    to_scale: number,
    duration_ms: number
  ): void
  {
    this.runCanvasAnim( dialog, [
      { transform: "scale(1)", opacity: 1, easing: this.EASE_OUT_QUAD },
      { transform: `scale(${to_scale})`, opacity: 0 },
    ], duration_ms, false );
  }

  // DOM layers a dialog keeps above the dialog canvas (e.g. a CSS scroll
  // container) are not covered by the canvas close animation. Run the same
  // shrink + fade on them around the same dialog center so both vanish as one.
  public static animateCloseDom(
    element: HTMLElement,
    dialog: AbstractDialog,
    to_scale: number,
    duration_ms: number
  ): Animation | null
  {
    if ( typeof element.animate !== "function" )
      return null;

    const center = dialog.getCenterGlobal();
    const canvas_left = Number.parseFloat( this.canvas.style.left ) || 0;
    const canvas_top = Number.parseFloat( this.canvas.style.top ) || 0;
    const element_left = Number.parseFloat( element.style.left ) || 0;
    const element_top = Number.parseFloat( element.style.top ) || 0;
    element.style.transformOrigin =
      `${canvas_left + center.x - element_left}px ${canvas_top + center.y - element_top}px`;

    return element.animate( [
      { transform: "scale(1)", opacity: 1, easing: this.EASE_OUT_QUAD },
      { transform: `scale(${to_scale})`, opacity: 0 },
    ], { duration: duration_ms, fill: "both" } );
  }

  public static animateReturnToTopCss(
    dialog: AbstractDialog,
    from_scale: number,
    final_scale: number,
    duration_ms: number
  ): void
  {
    this.runCanvasAnim( dialog, [
      { transform: `scale(${from_scale})`, opacity: 0, easing: this.EASE_OUT_QUAD },
      { transform: `scale(${final_scale})`, opacity: 1 },
    ], duration_ms, true );
  }

  // The first GPU raster of each dialog type stalls the main thread for
  // 300-500ms on weak devices (pipeline/texture warmup inside the raster
  // process). Rendering each hot dialog once into the hidden dialog canvas
  // during menu idle pays that cost off-screen.
  private static prewarming_dialog: AbstractDialog | null = null;
  private static prewarm_allowed: ( () => boolean ) | null = null;
  private static readonly PREWARM_DECODE_TIMEOUT_MS = 1500;
  private static readonly PREWARM_RASTER_OPACITY = 0.002;
  private static readonly PREWARM_MEMORY_CONSTRAINED_COUNT = 3;

  private static prewarm_log: string[] = [];

  public static getPrewarmLog(): string[]
  {
    return this.prewarm_log.slice();
  }

  private static notePrewarm( message: string ): void
  {
    this.prewarm_log.push( `${Math.round( performance.now() )} ${message}` );
    console.info( `[DialogManager] ${message}` );
  }

  public static setPrewarmAllowed( allowed: () => boolean ): void
  {
    this.prewarm_allowed = allowed;
  }

  private static abortDomPrewarm(): void
  {
    const dialog = this.prewarming_dialog;
    if ( !dialog )
      return;
    this.prewarming_dialog = null;
    this.callOnClose( dialog );
    resyncDomHost( this.canvas );
  }

  private static async prewarmDialogsDom( factories: Array<DialogPrewarmFactory> ): Promise<void>
  {
    const limit = isMemoryConstrained() ? this.PREWARM_MEMORY_CONSTRAINED_COUNT : factories.length;
    this.notePrewarm( `dom prewarm start: ${limit} dialogs` );
    for ( const factory of factories.slice( 0, limit ) )
    {
      const release = await this.waitForPrewarmSlot();
      if ( !release )
      {
        this.notePrewarm( "dom prewarm gave up: busy" );
        return;
      }
      if ( this.pool.has( factory.key ) )
      {
        release();
        continue;
      }

      let dialog: AbstractDialog;
      try
      {
        dialog = factory.create();
      }
      catch ( e )
      {
        console.warn( "[DialogManager] prewarm construct failed", e );
        release();
        continue;
      }

      this.prewarming_dialog = dialog;
      const started_at = performance.now();
      try
      {
        dialog.mountDomHost( this.canvas );
        dialog.render();

        const urls = new Set<string>();
        collectDomImageUrls( dialog.getDomNode(), urls );
        await this.decodePrewarmImages( urls );
        if ( this.prewarming_dialog !== dialog )
          continue;

        const node = dialog.getDomNode();
        showDomHostForRaster( this.canvas, this.PREWARM_RASTER_OPACITY );
        const { keyframes, duration_ms } = appearKeyframes( { from_scale: 0.7, overshoot_scale: 1.1, final_scale: 1, scale_up_ms: 136, settle_ms: 96 } );
        dialog.setSurfaceAnimation( runSurfaceAnimation( node, keyframes, duration_ms, true ) );
        for ( let i = 0; i < 3; i++ )
        {
          await waitForPresentedFrame();
          if ( this.prewarming_dialog !== dialog )
            break;
          dialog.update( performance.now() );
          dialog.render();
        }
        this.notePrewarm( `dom prewarm ${dialog.name()}: ${Math.round( performance.now() - started_at )} ms, ${urls.size} images` );
      }
      catch ( e )
      {
        console.warn( "[DialogManager] prewarm failed", e );
      }
      finally
      {
        if ( this.prewarming_dialog === dialog )
        {
          this.prewarming_dialog = null;
          dialog.removeCloseButtonPop();
          dialog.cancelSurfaceAnimation();
          if ( !this.recycle( dialog ) )
            this.destroyDialog( dialog );
          resyncDomHost( this.canvas );
        }
        release();
      }
    }
  }

  private static readonly PREWARM_RETRY_MS = 750;
  private static readonly PREWARM_MAX_WAIT_MS = 120_000;

  /** A BackgroundWork slot to release when the dialog is warmed, or null when the menu stayed busy. */
  private static async waitForPrewarmSlot(): Promise<( () => void ) | null>
  {
    const deadline = performance.now() + this.PREWARM_MAX_WAIT_MS;
    while ( performance.now() < deadline )
    {
      const release = await BackgroundWork.acquire( "dialog prewarm" );
      if ( !this.isAnyDialogOpened() && !this.prewarming_dialog && this.prewarm_allowed?.() !== false )
        return release;
      release();
      await new Promise<void>( resolve => setTimeout( resolve, this.PREWARM_RETRY_MS ) );
    }
    return null;
  }

  private static async decodePrewarmImages( urls: Set<string> ): Promise<void>
  {
    const pending: Promise<unknown>[] = [];
    for ( const url of urls )
    {
      let img = ImagesHolder.getDomImageByUrl( url );
      if ( !img )
      {
        img = new Image();
        img.src = url;
      }
      pending.push( img.decode().catch( () => null ) );
    }
    if ( pending.length === 0 )
      return;
    await Promise.race( [ Promise.all( pending ), new Promise( resolve => setTimeout( resolve, this.PREWARM_DECODE_TIMEOUT_MS ) ) ] );
  }

  public static async prewarmDialogs( factories: Array<DialogPrewarmFactory> ): Promise<void>
  {
    if ( this.dom_host )
    {
      await this.prewarmDialogsDom( factories );
      return;
    }

    if ( !this.overlay_is_visible )
    {
      this.overlay.style.opacity = "0.01";
      this.overlay.style.display = "block";
      await new Promise<void>( r => requestAnimationFrame( () => requestAnimationFrame( () => r() ) ) );
      if ( !this.overlay_is_visible )
      {
        this.overlay.style.display = "none";
        this.overlay.style.opacity = "0";
      }
    }

    for ( const factory of factories )
    {
      await ResourceManager.pacingDelay();

      // A real dialog owns the canvas now (or might grab it mid-item): stop
      // for good instead of racing it — a prewarm render would overwrite the
      // visible dialog and leave the canvas at prewarm opacity.
      if ( this.isAnyDialogOpened() )
        return;

      try
      {
        const dialog = factory.create();
        if ( this.isAnyDialogOpened() )
          return;
        this.canvas.style.opacity = "0.01";
        this.canvas.style.display = "block";
        dialog.render();
        await new Promise<void>( r => requestAnimationFrame( () => requestAnimationFrame( () => r() ) ) );
        this.callOnClose( dialog );
        if ( this.isAnyDialogOpened() )
          return;
        this.ctx.clearRect( 0, 0, this.width, this.height );
      }
      catch ( e )
      {
        console.warn( "[DialogManager] prewarm failed", e );
      }
      finally
      {
        this.canvas.style.opacity = "";
        if ( !this.isAnyDialogOpened() )
          this.canvas.style.display = "none";
      }
    }
  }

  private static callOnClose( closed: AbstractDialog | undefined ): void
  {
    if ( !closed )
      return;
    closed.removeCloseButtonPop();
    closed.cancelSurfaceAnimation();

    try { closed.onDialogWillClose(); }
    catch ( e ) { console.warn( "DialogManager: onDialogWillClose failed", e ); }

    if ( this.recycle( closed ) )
      return;

    this.destroyDialog( closed );
  }

  public static isAnyDialogOpened() { return this.dialog_stack.length > 0; }

  public static msSinceLastOpen(): number { return performance.now() - this.last_open_at; }

  public static getTopDialog(): AbstractDialog | null
  {
    return this.dialog_stack.length > 0 ? this.dialog_stack[this.dialog_stack.length - 1] : null;
  }

  public static getWidth() { return this.width; }

  public static getHeight() { return this.height; }

  private static isInteractionBlocked(): boolean
  {
    return this.is_route_transition_active
      || performance.now() < this.interaction_blocked_until_ms
      || ( this.dialog_stack.length > 0 && this.topDialog().isClosing() );
  }

  private static topDialog():AbstractDialog
  {
    if ( this.dialog_stack.length == 0 )
      throw new Error('topDialog(): No dialogs are open');

    return this.dialog_stack[this.dialog_stack.length - 1];
  }

  private static hide(): void
  {
    this.cancelCanvasAnim();
    this.canvas.style.opacity = "";
    this.overlay_is_visible = false;
    this.canvas.style.display = "none";
    snapBackdrop( this.overlay, 0 );
    this.overlay.style.display = "none";
  }

  private static show( should_animate_overlay: boolean = true ): void
  {
    const was_visible = this.overlay_is_visible;
    this.overlay_is_visible = true;
    if ( this.canvas.style.opacity === "0.01" )
      this.canvas.style.opacity = "";
    this.canvas.style.display = "block";

    if ( !should_animate_overlay || was_visible )
    {
      this.overlay.style.display = "block";
      snapBackdrop( this.overlay, 1 );
      return;
    }

    if ( this.overlay.style.display === "none" || !this.overlay.style.opacity )
      this.overlay.style.opacity = "0";
    this.overlay.style.display = "block";
    fadeBackdrop( this.overlay, 1, this.BACKDROP_FADE_MS );
  }
}
